'use client';

import { APP_SCORING_FORMAT, APP_STYLE_CONFIG } from '@/constants/config';
import { cn, getScoreCardDescription } from '@/utils';
import { COMMON_TEXT, STATUS_MESSAGES } from '@/constants';
import { FEATURE_FLAGS } from '@/constants/feature-flags';
import { NextRoleplayCard } from '@/components/Cards/NextRoleplayCard';
import { Scorecard } from '@/components/Score/Scorecard';
import { ScoreHistoryCard } from '@/components/Score/ScoreHistoryCard';
import { ScorePassFailCard } from '@/components/Score/ScorePassFailCard';
import { SimulationComplianceBanner } from '../SimulationComplianceBanner';
import { SimulationScoreFeedback } from '../SimulationScoreFeedback';
import { SimulationScoreHeader } from '../SimulationScoreHeader';
import { SimulationScoreProps } from './types';
import { useSimulationScoreFeature } from '@/features/simulations/simulation-score/useSimulationScoreFeature';
import React from 'react';

/**
 * This component displays the scorecard for a simulation, including the overall score,
 * category scores, feedback, and actions for retaking the role-play or viewing the certificate.
 * It also handles different states such as loading, score processing, and printing, and
 * adjusts the layout accordingly.
 */
export const SimulationScore: React.FC<SimulationScoreProps> = ({
  certificationId,
  certificationTitle,
  className,
  complianceFlagText = STATUS_MESSAGES.COMPLIANCE_REMINDERS,
  createdDate,
  isLoading,
  isPrinting: externalIsPrinting,
  layout = 'default',
  nextSimulation,
  onRetakeRolePlayClick,
  onViewCertificateClick,
  score,
  scoreIsProcessed,
  showHeader = true,
  showRetakeRolePlayButton = true,
  showScoreHistory = FEATURE_FLAGS.ENABLE_SALES_REP_SCORE_PAGE_HISTORY,
  simulation,
  simulationId,
  title: simulationTitle,
  useAnimatedNumbers = APP_STYLE_CONFIG.useAnimatedNumbers,
}) => {
  const {
    containerClasses,
    currentUser,
    currentUserFullName,
    handlePrintButtonClick,
    handleViewCertificateClick,
    isPrinting,
    isXlUp,
    layoutClasses,
    navigateToDashboard,
    nextRoleplayActions,
    pageSubtitleText,
    pageTitleText,
    printDate,
    printRef,
    retakeRoleplayActions,
    roleplayScorecardActions,
    scoreHistoryEntries,
    showComplianceFlag,
    showFailCard,
    showNextSimulationCard,
    showPassCard,
    showScoreHistoryCard,
  } = useSimulationScoreFeature({
    certificationId,
    certificationTitle,
    className,
    createdDate,
    externalIsPrinting,
    isLoading,
    layout,
    nextSimulation,
    onRetakeRolePlayClick,
    onViewCertificateClick,
    score,
    showRetakeRolePlayButton,
    showScoreHistory,
    simulation,
    simulationId,
    simulationTitle,
  });

  // When the active score is missing runtime cutoffs, fall back to any
  // same-context history entry cutoffs so score visuals stay aligned.
  const runtimeRatingCutoffs = React.useMemo(() => {
    if (score?.runtimeRatingCutoffs) {
      return score.runtimeRatingCutoffs;
    }

    const historyEntryWithCutoffs = scoreHistoryEntries.find((entry) => {
      return Boolean(entry.runtimeRatingCutoffs);
    });

    return historyEntryWithCutoffs?.runtimeRatingCutoffs;
  }, [score?.runtimeRatingCutoffs, scoreHistoryEntries]);

  return (
    <div
      className={cn('print-container relative mx-auto flex h-full flex-col', containerClasses)}
      data-testid="simulation-score"
      ref={printRef}
    >
      <div className="h-screen w-full flex-1">
        {showHeader && (
          <SimulationScoreHeader
            certificationTitle={certificationTitle}
            createdDate={createdDate}
            currentUserFullName={currentUserFullName}
            handlePrintButtonClick={handlePrintButtonClick}
            isLoading={isLoading}
            isPrinting={isPrinting}
            isXlUp={isXlUp}
            navigateToDashboard={navigateToDashboard}
            pageSubtitleText={pageSubtitleText}
            pageTitleText={pageTitleText}
            printDate={printDate}
            printRef={printRef}
            score={score}
            scoreIsProcessed={scoreIsProcessed}
            simulationTitle={simulationTitle}
          />
        )}

        {scoreIsProcessed && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6">
            <div className={layoutClasses.leftSide}>
              <div className="flex w-full flex-col gap-y-6">
                {showComplianceFlag && <SimulationComplianceBanner text={complianceFlagText} />}

                <SimulationScoreFeedback
                  complianceFeedback={score?.complianceReminders}
                  currentUser={currentUser}
                  isLoading={isLoading}
                  isPrinting={isPrinting}
                  score={score}
                  simulation={simulation}
                />

                {layout === 'default' && <div className="h-[100px] w-full" />}
              </div>

              {layout === 'compact' && (
                <>
                  {showScoreHistoryCard && (
                    <div className="mt-6">
                      <ScoreHistoryCard
                        entries={scoreHistoryEntries}
                        isLoading={isLoading}
                        simulationType={simulation?.type}
                        withCardBorder={false}
                      />
                    </div>
                  )}

                  {showNextSimulationCard && showRetakeRolePlayButton && (
                    <div className="mt-6">
                      <NextRoleplayCard
                        nextRoleplayActions={retakeRoleplayActions}
                        nextSimulationTitle={nextSimulation?.title}
                      />
                    </div>
                  )}

                  <div className="h-[100px] w-full" />
                </>
              )}
            </div>
            <div className={layoutClasses.rightSide}>
              <div className="flex w-full flex-col gap-y-4">
                <div>
                  {showPassCard && (
                    <ScorePassFailCard
                      cardType="pass"
                      isLoading={isLoading}
                      onViewCertificateClick={handleViewCertificateClick}
                      score={
                        APP_SCORING_FORMAT === 'percentage' ? score?.scorePercentage : score?.score
                      }
                      showRetakeRolePlayButton={showRetakeRolePlayButton}
                      simulationType={simulation?.type}
                    />
                  )}

                  {showFailCard && (
                    <ScorePassFailCard
                      cardType="fail"
                      isLoading={isLoading}
                      onCertificationPracticeClick={navigateToDashboard}
                      score={
                        APP_SCORING_FORMAT === 'percentage' ? score?.scorePercentage : score?.score
                      }
                      showRetakeRolePlayButton={showRetakeRolePlayButton}
                      simulationType={simulation?.type}
                    />
                  )}

                  {!showPassCard && !showFailCard && (
                    <Scorecard
                      actions={roleplayScorecardActions}
                      className="max-w-none"
                      description={getScoreCardDescription(score)}
                      isLoading={isLoading}
                      isPrinting={isPrinting}
                      maxScore={score?.scoreMax}
                      score={score?.score}
                      scoreCardId={score?.id}
                      scorePercentage={score?.scorePercentage}
                      runtimeRatingCutoffs={runtimeRatingCutoffs}
                      sessionId={score?.sessionId}
                      sharedWithManager={score?.sharedWithManager || false}
                      simulationType={simulation?.type}
                      size={layout === 'compact' ? 'compact' : 'standard'}
                      skillLevelScaleClassName={layout === 'compact' ? 'max-w-md' : ''}
                      title={COMMON_TEXT.PRACTICE_SCORE}
                      useAnimatedNumbers={useAnimatedNumbers}
                      userId={currentUser?.uuid}
                    />
                  )}
                </div>

                {layout === 'default' && (
                  <>
                    {showScoreHistoryCard && (
                      <div>
                        <ScoreHistoryCard
                          entries={scoreHistoryEntries}
                          isLoading={isLoading}
                          simulationType={simulation?.type}
                          withCardBorder={false}
                        />
                      </div>
                    )}

                    {showNextSimulationCard && (
                      <div>
                        <NextRoleplayCard
                          nextRoleplayActions={nextRoleplayActions}
                          nextSimulationTitle={nextSimulation?.title}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SimulationScore.displayName = 'SimulationScore';

export default SimulationScore;
